// The suggestions of one dynamic code frame, and every decision a Manager
// makes on them (docs/autocoder-dynamic-frames.md). Accepting with
// "Re-code since" also recodes the framework on each linked pipeline through
// the recode route (POST /pipelines/:id/recode, dry run first).

import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'
import { linkedPipelineFrames, LinkedPipelineFrame, RecodeEstimate } from '../utils/dynamicFrames'
import type { AutoCoderCode, AutoCoderFramework } from '../types/gotypes'
import useSnackbar from './useSnackbar'

export interface FrameSuggestionExample {
  record_id: string
  text: string
}

export interface FrameSuggestion {
  id: string
  framework_id: string
  label: string
  description: string
  examples: FrameSuggestionExample[]
  count: number
  first_seen: number
  last_seen: number
  status: 'pending' | 'accepted' | 'rejected' | 'merged'
  merged_into_id?: string
  accepted_code?: string
  decided_by?: string
  decided_at?: number
}

export interface FrameworkVersion {
  version: number
  createdAt: string
  createdBy: string
  addedCodes: { code: string; text: string }[]
  note?: string
}

export interface LinkedPipelineUpdate {
  pipeline_id: string
  name?: string
  framework: string
  updated: boolean
  skipped?: string
}

export interface AcceptResult {
  code: AutoCoderCode
  version: number
  pipelines: LinkedPipelineUpdate[]
}

export interface AcceptInput {
  label: string
  description: string
  mode: 'forward' | 'recode'
  since?: string
}

interface PipelineLike {
  id: string
  config?: { name?: string; frameworks?: AutoCoderFramework[] | null } | null
}

export function useFrameSuggestions(customer: string, job: string, frameworkId: string) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const base = `${API_BASE_URL}/autocoder/${customer}/${job}`
  const enabled = Boolean(customer && job && frameworkId)
  const suggestionsKey = ['frame-suggestions', { customer, job, frameworkId }]
  const versionsKey = ['framework-versions', { customer, job, frameworkId }]

  const suggestionsQuery = useQuery({
    queryKey: suggestionsKey,
    enabled,
    queryFn: async () => (await axios.get<FrameSuggestion[]>(`${base}/frameworks/${frameworkId}/suggestions`)).data,
  })

  const versionsQuery = useQuery({
    queryKey: versionsKey,
    enabled,
    queryFn: async () => (await axios.get<FrameworkVersion[]>(`${base}/frameworks/${frameworkId}/versions`)).data,
  })

  // The pipelines whose copies link to this frame: the ones an accepted code
  // reaches, and the ones "Re-code since" recodes.
  const linkedQuery = useQuery({
    queryKey: ['frame-linked-pipelines', { customer, job, frameworkId }],
    enabled,
    queryFn: async (): Promise<LinkedPipelineFrame[]> => {
      const res = await axios.get<{ items: PipelineLike[] }>(`${base}/pipelines`, { params: { limit: 500 } })
      return linkedPipelineFrames(res.data.items ?? [], frameworkId)
    },
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: suggestionsKey })
    queryClient.invalidateQueries({ queryKey: versionsKey })
    queryClient.invalidateQueries({ queryKey: ['frameworks'] })
    queryClient.invalidateQueries({ queryKey: ['framework'] })
  }
  const onError = (error: unknown) => snackbar.error(extractErrorMessage(error))

  const accept = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: AcceptInput }) =>
      (await axios.post<AcceptResult>(`${base}/frameworks/${frameworkId}/suggestions/${id}/accept`, input)).data,
    onSuccess: refresh,
    onError,
  })
  const reject = useMutation({
    mutationFn: async (id: string) => (await axios.post<FrameSuggestion>(`${base}/frameworks/${frameworkId}/suggestions/${id}/reject`, {})).data,
    onSuccess: () => {
      refresh()
      snackbar.success('Suggestion rejected')
    },
    onError,
  })
  const merge = useMutation({
    mutationFn: async ({ id, code }: { id: string; code: string }) =>
      (await axios.post<FrameSuggestion>(`${base}/frameworks/${frameworkId}/suggestions/${id}/merge`, { code })).data,
    onSuccess: () => {
      refresh()
      snackbar.success('Suggestion merged into the code')
    },
    onError,
  })
  const rename = useMutation({
    mutationFn: async ({ id, label, description }: { id: string; label: string; description: string }) =>
      (await axios.patch<FrameSuggestion>(`${base}/frameworks/${frameworkId}/suggestions/${id}`, { label, description })).data,
    onSuccess: () => {
      refresh()
      snackbar.success('Suggestion renamed')
    },
    onError,
  })
  const consolidate = useMutation({
    mutationFn: async () =>
      (await axios.post<{ considered: number; groups: number; merged: number }>(`${base}/frameworks/${frameworkId}/suggestions/consolidate`, {})).data,
    onSuccess: (res) => {
      refresh()
      snackbar.success(res.groups ? `Grouped ${res.merged + res.groups} suggestions into ${res.groups}` : 'No look-alike suggestions to group')
    },
    onError,
  })

  /** The recode route's dry run for one linked pipeline framework. */
  const estimateRecode = async (linked: LinkedPipelineFrame, since: string): Promise<RecodeEstimate> =>
    (await axios.post<RecodeEstimate>(`${base}/pipelines/${linked.pipelineId}/recode`, { framework: linked.framework, since, dryRun: true })).data

  /** Starts the recode for one linked pipeline framework. */
  const startRecode = async (pipelineId: string, framework: string, since: string): Promise<{ records: number; batches: number }> =>
    (await axios.post<{ records: number; batches: number }>(`${base}/pipelines/${pipelineId}/recode`, { framework, since, dryRun: false })).data

  return {
    suggestions: suggestionsQuery.data ?? [],
    suggestionsLoading: suggestionsQuery.isLoading,
    suggestionsError: suggestionsQuery.error,
    versions: versionsQuery.data ?? [],
    linked: linkedQuery.data ?? [],
    accept: (id: string, input: AcceptInput) => accept.mutateAsync({ id, input }),
    reject: (id: string) => reject.mutateAsync(id),
    merge: (id: string, code: string) => merge.mutateAsync({ id, code }),
    rename: (id: string, label: string, description: string) => rename.mutateAsync({ id, label, description }),
    consolidate: () => consolidate.mutateAsync(),
    consolidating: consolidate.isPending,
    busy: accept.isPending || reject.isPending || merge.isPending || rename.isPending,
    estimateRecode,
    startRecode,
  }
}

export default useFrameSuggestions
